import {useCallback, useState, useEffect} from 'react';

// Custom hook to encapsulate groups and exclude state management
// Mirrors existing logic in SegmentsCard to avoid behavior/UI changes
export default function useSegmentsGroups({
  handleSegmentsChange,
  handleExcludedSegmentsChange,
  formData,
  segments,
  isActivityComponent = false
}) {
  const [groups, setGroups] = useState([]); // [{id, items: [{id, name}]}]
  const [activeGroupIndex, setActiveGroupIndex] = useState(null);

  // Exclude state (separate from groups)
  const [excludeSelected, setExcludeSelected] = useState([]);

  // Modal visibilities
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showExcludeModal, setShowExcludeModal] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);

  const processSegmentItem = useCallback(
    (seg) => {
      if (typeof seg === 'object' && seg?.name) {
        return {id: seg?.id || seg?.value, label: seg?.name};
      } else if (typeof seg === 'object' && seg?.label) {
        return {id: seg?.id || seg?.value, label: seg?.label};
      } else {
        const segmentData = segments?.find(
          (s) => s.id === seg || s.value === seg
        );
        return {id: seg, label: segmentData?.name || segmentData?.label || seg};
      }
    },
    [segments]
  );

  // Helper function to process segment groups
  const processSegmentGroup = useCallback(
    (segmentGroup) => {
      const items = [];

      if (Array.isArray(segmentGroup)) {
        segmentGroup.forEach((seg) => {
          items.push(processSegmentItem(seg));
        });
      } else {
        items.push(processSegmentItem(segmentGroup));
      }

      return items;
    },
    [processSegmentItem]
  );

  useEffect(() => {
    // Helper function to process individual segment items

    if (formData && Object.keys(formData)?.length > 0) {
      const segmentsData = formData?.selectedSegments || formData?.segments;

      if (Array.isArray(segmentsData) && segmentsData?.length > 0) {
        if (isActivityComponent) {
          // For activity components, flatten all segments into a single group
          const allSegmentItems = [];
          segmentsData.forEach((segmentGroup) => {
            allSegmentItems.push(...processSegmentGroup(segmentGroup));
          });

          setGroups([{id: 1, items: allSegmentItems}]);
        } else {
          // For non-activity components, maintain separate groups
          const initialGroups = segmentsData.map((segmentGroup, index) => ({
            id: index + 1,
            items: processSegmentGroup(segmentGroup)
          }));

          setGroups(initialGroups);
        }
      } else {
        setGroups([]);
      }

      if (
        Array.isArray(formData.excludedSegments) &&
        formData.excludedSegments.length > 0
      ) {
        const initialExcluded = formData.excludedSegments.map((seg) => ({
          id: seg.value || seg.id || seg,
          label: seg.label || seg.value || seg.id || seg
        }));
        setExcludeSelected(initialExcluded);
      } else {
        setExcludeSelected([]);
      }

      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [
    formData,
    segments,
    isInitialized,
    isActivityComponent,
    processSegmentGroup
  ]);

  // Flatten and notify parent for backward compatibility
  const notifyParent = useCallback(
    (newGroups) => {
      // Send grouped arrays of ids, excluding empty groups
      const groupedIds = newGroups
        .filter((g) => Array.isArray(g.items) && g.items.length > 0)
        .map((g) => {
          const ids = g.items.map((s) => s.id);
          const uniqueIds = Array.from(new Set(ids));
          return uniqueIds;
        });
      handleSegmentsChange(groupedIds);
    },
    [handleSegmentsChange]
  );

  const notifyExcludedParent = useCallback(
    (newExcluded) => {
      const mapped = newExcluded.map((s) => ({label: s.label, value: s.id}));
      handleExcludedSegmentsChange(mapped);
    },
    [handleExcludedSegmentsChange]
  );

  const handleCancel = useCallback(() => {
    setShowSegmentModal(false);
    setShowExcludeModal(false);
    setActiveGroupIndex(null);
  }, []);

  const applyInclude = useCallback(
    (selection) => {
      // Expect payload from SegmentsModal: { ids: Set, items: Array<{id, label}> }
      // Defensive fallback keeps only {id,label}
      const items = Array.isArray(selection?.items) ? selection.items : [];
      const ids =
        selection?.ids instanceof Set ? Array.from(selection.ids) : [];
      const base =
        items.length > 0 ? items : ids.map((id) => ({id, label: String(id)}));
      const segments = base.map((it) => ({id: it.id, label: it.label || ''}));
      setGroups((prev) => {
        let next = [...prev];

        if (isActivityComponent) {
          if (next?.length === 0) {
            next = [{id: 1, items: segments}];
          } else {
            const existingItems = next[0]?.items || [];
            const allItems = [...existingItems, ...segments];
            const uniqueItems = allItems?.filter(
              (item, index, self) =>
                index === self?.findIndex((t) => t?.id === item?.id)
            );
            next = [{id: 1, items: uniqueItems}];
          }
        } else {
          // For Reward: original group behavior
          if (next.length === 0 && activeGroupIndex === null) {
            next = [{id: 1, items: segments}];
          } else if (activeGroupIndex !== null) {
            next[activeGroupIndex] = {
              ...next[activeGroupIndex],
              items: segments
            };
          } else {
            // Fallback create first group
            next = [{id: 1, items: segments}];
          }
        }

        notifyParent(next);
        return next;
      });
      setShowSegmentModal(false);
      setActiveGroupIndex(null);
    },
    [activeGroupIndex, notifyParent, isActivityComponent]
  );

  const applyExclude = useCallback(
    (selection) => {
      // Expect payload from SegmentsModal: { ids: Set, items: Array<{id, label}> }
      const items = Array.isArray(selection?.items) ? selection.items : [];
      const ids =
        selection?.ids instanceof Set ? Array.from(selection.ids) : [];
      const base =
        items.length > 0 ? items : ids.map((id) => ({id, label: String(id)}));
      const segments = base.map((it) => ({id: it.id, label: it.label || ''}));
      setExcludeSelected(segments);
      notifyExcludedParent(segments);
      setShowExcludeModal(false);
    },
    [notifyExcludedParent]
  );

  const addGroup = useCallback(() => {
    setGroups((prev) => [...prev, {id: prev.length + 1, items: []}]);
  }, []);

  const deleteGroup = useCallback(
    (index) => {
      setGroups((prev) => {
        const next = prev
          .filter((_, i) => i !== index)
          .map((g, i2) => ({...g, id: i2 + 1}));
        notifyParent(next);
        return next;
      });
    },
    [notifyParent]
  );

  const removeChip = useCallback(
    (groupIndex, segId) => {
      setGroups((prev) => {
        const next = [...prev];
        next[groupIndex] = {
          ...next[groupIndex],
          items: next[groupIndex].items.filter((s) => s.id !== segId)
        };
        notifyParent(next);
        return next;
      });
    },
    [notifyParent]
  );

  const removeExcludeChip = useCallback(
    (segId) => {
      setExcludeSelected((prev) => {
        const next = prev.filter((s) => s.id !== segId);
        notifyExcludedParent(next);
        return next;
      });
    },
    [notifyExcludedParent]
  );

  const openGroupModal = useCallback((index) => {
    setActiveGroupIndex(index);
    setShowSegmentModal(true);
  }, []);

  const openFirstSelection = useCallback(() => {
    setActiveGroupIndex(null);
    setShowSegmentModal(true);
  }, []);

  const groupPlaceholder = useCallback((items) => {
    if (!items || items.length === 0) return 'Select Segment(s)';
    if (items.length === 1) return items[0].label;
    return `Multiple Selected`;
  }, []);

  // Optional: could initialize from props or external form data in the future when contract is available

  return {
    // state
    groups,
    activeGroupIndex,
    excludeSelected,
    showSegmentModal,
    showExcludeModal,

    // setters
    setShowExcludeModal,

    // actions
    handleCancel,
    applyInclude,
    applyExclude,
    addGroup,
    deleteGroup,
    removeChip,
    removeExcludeChip,
    openGroupModal,
    openFirstSelection,
    groupPlaceholder
  };
}
